import { useEffect, useState } from "react";
import axios from "axios";
import { useAccountant } from "@/contexts/AccountantContext";
import { useAuth } from "@/contexts/AuthContext";
import RenameAccountantModal from "@/components/RenameAccountantModal";
import usePushNotifications from "@/hooks/usePushNotifications";
import { clearSetupReminderDismiss, isProfileSetupComplete } from "@/lib/setupCompleteness";
import { formatCardCvv, formatCardExpiry, formatCardNumber } from "@/lib/cardInput";
import { isLogoTooLarge, logoTooLargeMessage, LOGO_MAX_LABEL } from "@/lib/logoUpload";
import { INVOICE_TEMPLATES } from "@/lib/invoiceTemplates";
import { openAuthenticatedPdf } from "@/lib/downloadExport";
import KipaIcon from "@/components/kipa/KipaIcon";
import { Building2, Check, Eye, Pencil, Send, Sun } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SettingsScreen({ onClose }) {
  const { name: accountantName, fmt, refresh } = useAccountant();
  const { user, logout } = useAuth();
  const push = usePushNotifications();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [nudgeSending, setNudgeSending] = useState(false);
  const [lastNudge, setLastNudge] = useState(null);
  const [showRename, setShowRename] = useState(false);
  const [toast, setToast] = useState("");
  const [paymentTab, setPaymentTab] = useState("bank");

  useEffect(() => {
    axios.get(`${API}/settings`).then((r) => {
      const d = { ...r.data };
      if (d.twilio_auth_token === "••••••••") d.twilio_auth_token = "";
      if (d.card_cvv === "•••") d.card_cvv = "";
      if (d.card_number) d.card_number = formatCardNumber(d.card_number);
      setForm(d);
    });
    axios.get(`${API}/nudge/history`).then((r) => setLastNudge(r.data[0] || null));
  }, []);

  if (!form) {
    return <div className="p-8 text-center text-[var(--tx3)]">Loading…</div>;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const patch = { ...form };
      delete patch.id;
      delete patch.updated_at;
      delete patch.twilio_configured;
      if (!patch.twilio_auth_token) delete patch.twilio_auth_token;
      if (!patch.card_cvv) delete patch.card_cvv;
      if (patch.card_number) patch.card_number = patch.card_number.replace(/\D/g, "");
      const r = await axios.put(`${API}/settings`, patch);
      setForm({
        ...r.data,
        twilio_auth_token: "",
        card_cvv: "",
        card_number: r.data.card_number ? formatCardNumber(r.data.card_number) : "",
      });
      await refresh();
      if (isProfileSetupComplete(r.data)) {
        clearSetupReminderDismiss(user?.id || user?.email);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const triggerNudge = async () => {
    setNudgeSending(true);
    try {
      const r = await axios.post(`${API}/nudge/send-now`);
      setLastNudge(r.data);
    } finally {
      setNudgeSending(false);
    }
  };

  return (
    <div className="flex flex-col h-full bg-[var(--bg2)]" data-testid="settings-screen">
      <div className="bg-white px-4 py-3 border-b border-[var(--br)] flex items-center gap-3">
        <button
          onClick={onClose}
          data-testid="settings-close"
          className="w-9 h-9 rounded-xl bg-[var(--sf2)] border border-[var(--br)] flex items-center justify-center text-[var(--tx2)]"
        >
          ←
        </button>
        <div className="flex-1">
          <div className="text-[var(--tx)] font-extrabold text-[16px]">Settings</div>
          <div className="text-[11px] text-[var(--tx3)]">Business info · Notifications · Preferences</div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="settings-save"
          className="px-4 py-2 rounded-xl text-[12px] font-extrabold text-white bg-[var(--ac)] disabled:opacity-60"
        >
          {saving ? "Saving…" : saved ? (
            <span className="inline-flex items-center gap-1">
              Saved <KipaIcon icon={Check} size="xs" color="#fff" />
            </span>
          ) : "Save"}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {user && (
          <div className="bg-white border border-[var(--br)] rounded-2xl p-3 mb-4 flex items-center justify-between">
            <div>
              <div className="text-[12px] text-[var(--tx3)]">Signed in as</div>
              <div className="text-[14px] font-bold text-[var(--tx)]">{user.owner_name}</div>
              <div className="text-[11px] text-[var(--tx3)]">{user.email}</div>
            </div>
            <button
              type="button"
              onClick={logout}
              className="text-[12px] font-bold text-[var(--rd)] px-3 py-2 rounded-xl border border-[var(--rl)]"
              data-testid="settings-logout"
            >
              Log out
            </button>
          </div>
        )}
        {/* Your Accountant */}
        <Section title="Your Accountant">
          <button
            onClick={() => setShowRename(true)}
            data-testid="settings-rename-accountant"
            className="w-full flex items-center justify-between bg-[var(--sf2)] border border-[var(--br)] rounded-xl px-4 py-3"
          >
            <div className="text-left">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-0.5">Accountant Name</div>
              <div className="text-[15px] font-extrabold text-[var(--tx)]" data-testid="settings-accountant-name">{accountantName}</div>
            </div>
            <KipaIcon icon={Pencil} size="md" color="var(--color-primary)" />
          </button>
        </Section>

        {/* Business */}
        <Section title="Business">
          <Field label="Business name">
            <input
              className="s-inp"
              value={form.business_name || ""}
              onChange={(e) => set("business_name", e.target.value)}
              data-testid="field-business-name"
            />
          </Field>
          <Field label="Business logo / profile picture" hint={`Max ${LOGO_MAX_LABEL}. PNG or JPG recommended.`}>
            <div className="flex items-center gap-3">
              <div
                className="w-14 h-14 rounded-xl border border-[var(--br)] bg-[var(--sf2)] overflow-hidden flex items-center justify-center flex-shrink-0"
                data-testid="settings-logo-preview"
              >
                {form.business_logo ? (
                  <img src={form.business_logo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <KipaIcon icon={Building2} size="lg" color="var(--color-primary)" />
                )}
              </div>
              <div className="flex flex-col gap-1">
                <label className="inline-block bg-[var(--ac)] text-white rounded-xl px-4 py-2 text-[12px] font-bold cursor-pointer w-fit">
                  Change logo
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    data-testid="settings-logo-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (!file.type.startsWith("image/")) {
                        alert("Please choose an image file.");
                        return;
                      }
                      if (isLogoTooLarge(file)) {
                        alert(logoTooLargeMessage());
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = () => set("business_logo", String(reader.result || ""));
                      reader.readAsDataURL(file);
                    }}
                  />
                </label>
                {form.business_logo && (
                  <button
                    type="button"
                    className="text-[11px] text-[var(--tx3)] font-semibold text-left"
                    data-testid="settings-logo-remove"
                    onClick={() => set("business_logo", "")}
                  >
                    Remove logo
                  </button>
                )}
              </div>
            </div>
          </Field>
          <Field label="Phone number">
            <input
              className="s-inp"
              value={form.owner_phone || ""}
              onChange={(e) => set("owner_phone", e.target.value)}
              placeholder="+234..."
              data-testid="field-owner-phone"
            />
          </Field>
          <Field label="Business address">
            <textarea
              className="s-inp min-h-[72px] resize-none"
              value={form.business_address || ""}
              onChange={(e) => set("business_address", e.target.value)}
              data-testid="field-business-address"
            />
          </Field>
          <Field label="Owner WhatsApp number" hint="E.164 format, e.g. +2348012345678">
            <input
              className="s-inp"
              value={form.owner_whatsapp || ""}
              onChange={(e) => set("owner_whatsapp", e.target.value)}
              placeholder="+234..."
              data-testid="field-owner-whatsapp"
            />
          </Field>
        </Section>

        {/* Invoicing */}
        <Section title="Invoices" hint="Choose a PDF design and signature for every invoice you send.">
          <Field label="Invoice template" hint="Tap Preview to see your selection — Save to apply to all invoices.">
            <div className="grid grid-cols-2 gap-2">
              {INVOICE_TEMPLATES.map((tpl) => {
                const active = (form.invoice_template || "classic") === tpl.id;
                return (
                  <button
                    key={tpl.id}
                    type="button"
                    onClick={() => set("invoice_template", tpl.id)}
                    data-testid={`invoice-template-${tpl.id}`}
                    className={`rounded-xl p-3 text-left border transition-all ${
                      active
                        ? "border-[var(--ac)] bg-[var(--al)]"
                        : "border-[var(--br)] bg-white"
                    }`}
                  >
                    <div className="mb-1">
                      <KipaIcon icon={tpl.icon} size="md" color="var(--color-primary)" />
                    </div>
                    <div className="text-[12px] font-extrabold text-[var(--tx)]">{tpl.name}</div>
                    <div className="text-[10px] text-[var(--tx3)] mt-0.5 leading-snug">{tpl.desc}</div>
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Payment due (days after invoice)" hint="Default due date on new invoices.">
            <input
              className="s-inp"
              type="number"
              min={1}
              max={90}
              value={form.invoice_due_days ?? 14}
              onChange={(e) => set("invoice_due_days", parseInt(e.target.value, 10) || 14)}
              data-testid="field-invoice-due-days"
            />
          </Field>
          <Field label="Signature name" hint="Printed on invoice PDFs below the signature line.">
            <input
              className="s-inp"
              value={form.signature_name || ""}
              onChange={(e) => set("signature_name", e.target.value)}
              placeholder={form.business_name || "Your name or business name"}
              data-testid="field-signature-name"
            />
          </Field>
          <button
            type="button"
            onClick={async () => {
              try {
                const params = new URLSearchParams({
                  template: form.invoice_template || "classic",
                  signature_name: form.signature_name || form.business_name || "",
                  due_days: String(form.invoice_due_days ?? 14),
                  t: String(Date.now()),
                });
                await openAuthenticatedPdf(`${API}/invoices/preview-pdf?${params}`);
              } catch (e) {
                alert(e.message || "Could not open preview. Try again.");
              }
            }}
            className="w-full mt-1 py-3 rounded-xl bg-[var(--ac)] text-white text-[13px] font-extrabold inline-flex items-center justify-center gap-2"
            data-testid="invoice-template-preview"
          >
            <KipaIcon icon={Eye} size="sm" color="#fff" />
            Preview {INVOICE_TEMPLATES.find((t) => t.id === (form.invoice_template || "classic"))?.name || "Classic"} template
          </button>
        </Section>

        {/* Payment details */}
        <Section title="Payment Details" hint="Shown on invoice PDFs and WhatsApp messages so customers know how to pay you.">
          <div className="flex gap-2 mb-3">
            {[
              { k: "bank", label: "Bank transfer" },
              { k: "card", label: "Card" },
            ].map((opt) => (
              <button
                key={opt.k}
                type="button"
                onClick={() => setPaymentTab(opt.k)}
                data-testid={`settings-payment-tab-${opt.k}`}
                className={`px-3.5 py-2 rounded-full text-[12px] font-semibold transition-all ${
                  paymentTab === opt.k
                    ? "bg-[var(--ac)] text-white"
                    : "bg-[var(--sf2)] text-[var(--tx2)] border border-[var(--br)]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {paymentTab === "bank" ? (
            <>
              <Field label="Account name">
                <input
                  className="s-inp"
                  value={form.bank_account_name || ""}
                  onChange={(e) => set("bank_account_name", e.target.value)}
                  data-testid="field-bank-account-name"
                />
              </Field>
              <Field label="Account number">
                <input
                  className="s-inp"
                  value={form.bank_account_number || ""}
                  onChange={(e) => set("bank_account_number", e.target.value)}
                  data-testid="field-bank-account-number"
                />
              </Field>
              <Field label="Bank name">
                <input
                  className="s-inp"
                  value={form.bank_name || ""}
                  onChange={(e) => set("bank_name", e.target.value)}
                  placeholder="e.g. GTBank, Access Bank"
                  data-testid="field-bank-name"
                />
              </Field>
            </>
          ) : (
            <>
              <Field label="Name on card">
                <input
                  className="s-inp"
                  value={form.card_holder_name || ""}
                  onChange={(e) => set("card_holder_name", e.target.value)}
                  autoComplete="cc-name"
                  data-testid="field-card-holder-name"
                />
              </Field>
              <Field label="Card number">
                <input
                  className="s-inp"
                  value={form.card_number || ""}
                  onChange={(e) => set("card_number", formatCardNumber(e.target.value))}
                  placeholder="4242 4242 4242 4242"
                  inputMode="numeric"
                  autoComplete="cc-number"
                  data-testid="field-card-number"
                />
              </Field>
              <div className="flex gap-3">
                <Field label="Expiry (MM/YY)">
                  <input
                    className="s-inp"
                    value={form.card_expiry || ""}
                    onChange={(e) => set("card_expiry", formatCardExpiry(e.target.value))}
                    placeholder="MM/YY"
                    inputMode="numeric"
                    autoComplete="cc-exp"
                    data-testid="field-card-expiry"
                  />
                </Field>
                <Field label="CVV">
                  <input
                    className="s-inp"
                    value={form.card_cvv || ""}
                    onChange={(e) => set("card_cvv", formatCardCvv(e.target.value))}
                    placeholder="123"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    data-testid="field-card-cvv"
                  />
                </Field>
              </div>
            </>
          )}
        </Section>

        <Section title="People Owing You" hint={`${accountantName} reminds you when customers owe you money. Only sent when someone actually owes you.`}>
          <label className="flex items-center gap-3 mb-3 cursor-pointer" data-testid="field-nudge-toggle">
            <input
              type="checkbox"
              checked={!!form.daily_nudge_enabled}
              onChange={(e) => set("daily_nudge_enabled", e.target.checked)}
              className="w-5 h-5 accent-[var(--ac)]"
            />
            <span className="text-[14px] text-[var(--tx)] font-semibold">
              Remind me about money people owe me
            </span>
          </label>
          <Field label="Reminder time" hint="Uses your local timezone">
            <input
              type="time"
              className="s-inp"
              value={form.daily_nudge_time || "09:00"}
              onChange={(e) => set("daily_nudge_time", e.target.value)}
              data-testid="field-nudge-time"
            />
          </Field>
          <Field label="Your timezone">
            <input
              className="s-inp"
              value={form.user_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone}
              onChange={(e) => set("user_timezone", e.target.value)}
              placeholder="Africa/Lagos"
              data-testid="field-user-timezone"
            />
          </Field>

          <button
            onClick={triggerNudge}
            disabled={nudgeSending}
            data-testid="nudge-send-now"
            className="btn-orange w-full mt-2"
          >
            {nudgeSending ? "Sending…" : "Send me a test reminder now"}
          </button>

          {lastNudge && (
            <div className="mt-3 rounded-xl p-3 text-[12px] border" style={{
              background: lastNudge.status === "sent" ? "var(--gl)" :
                          lastNudge.status === "skipped" ? "var(--gdl)" : "var(--bll)",
              borderColor: "var(--br)",
              color: "var(--tx2)",
            }}>
              <div className="font-bold mb-1 capitalize" style={{
                color: lastNudge.status === "sent" ? "var(--gn)" :
                       lastNudge.status === "skipped" ? "var(--go)" : "var(--bl)",
              }}>
                Last reminder: {lastNudge.status}
              </div>
              {lastNudge.error && <div className="text-[11px]">{lastNudge.error}</div>}
              {lastNudge.status !== "skipped" && (
                <div className="text-[11px]">
                  People owing you: {fmt(lastNudge.unpaid_amount)} · {lastNudge.unpaid_count} people
                </div>
              )}
            </div>
          )}
        </Section>

        {/* Web Push Notifications */}
        <Section title="Push Notifications" hint="Receive summaries and alerts on this device — even when KipaBiz is closed.">
          {!push.supported ? (
            <div className="text-[12px] text-[var(--tx3)] bg-[var(--sf2)] rounded-xl p-3" data-testid="push-unsupported-settings">
              Your browser doesn't support web push. Try Chrome or Firefox on Android.
            </div>
          ) : push.permission === "denied" ? (
            <div className="text-[12px] text-[var(--rd)] bg-[var(--rl)] rounded-xl p-3" data-testid="push-denied-settings">
              Notifications are blocked in your browser settings. Unblock them for this site to re-enable.
            </div>
          ) : (
            <>
              <div
                className="flex items-center justify-between gap-3 bg-[var(--sf2)] border border-[var(--br)] rounded-xl px-4 py-3 mb-3"
                data-testid="push-toggle-row"
              >
                <div className="flex-1">
                  <div className="text-[13px] font-bold text-[var(--tx)]">
                    {push.subscribed ? "Notifications enabled" : "Enable notifications"}
                  </div>
                  <div className="text-[11px] text-[var(--tx3)] mt-0.5">
                    {push.subscribed
                      ? "You'll get daily summaries and updates in the app"
                      : "Tap the switch to allow alerts on this device"}
                  </div>
                </div>
                <button
                  onClick={() => (push.subscribed ? push.unsubscribe() : push.subscribe())}
                  disabled={push.busy}
                  data-testid="push-toggle-btn"
                  className="relative w-12 h-7 rounded-full transition-colors disabled:opacity-50"
                  style={{ background: push.subscribed ? "var(--ac)" : "var(--br2)" }}
                >
                  <span
                    className="absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all"
                    style={{ left: push.subscribed ? "22px" : "2px" }}
                  />
                </button>
              </div>

              {push.subscribed && (
                <div className="space-y-2">
                  <button
                    onClick={() => push.sendTest()}
                    data-testid="push-test-btn"
                    className="w-full bg-[var(--sf2)] border border-[var(--br)] text-[var(--tx2)] rounded-xl py-2.5 font-bold text-[12px] inline-flex items-center justify-center gap-2"
                  >
                    <KipaIcon icon={Send} size="sm" color="var(--color-primary)" />
                    Send me a test notification
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/push/send-daily-summary`);
                      } catch (e) { /* ignore */ }
                    }}
                    data-testid="push-send-daily-summary-btn"
                    className="w-full bg-[var(--ac)] text-white rounded-xl py-2.5 font-extrabold text-[12px] inline-flex items-center justify-center gap-2"
                  >
                    <KipaIcon icon={Sun} size="sm" color="#fff" />
                    Preview today's 9 AM summary
                  </button>
                  <div className="text-[10px] text-[var(--tx3)] leading-snug mt-1">
                    Daily summary is scheduled at <b>{form.daily_nudge_time?.slice(0, 5) || "09:00"}</b> in your timezone.
                  </div>
                </div>
              )}

              {push.error && (
                <div className="mt-2 text-[11px] text-[var(--rd)]" data-testid="push-error">{push.error}</div>
              )}
            </>
          )}
        </Section>

        <div className="text-center text-[var(--tx3)] text-[11px] mt-6 mb-2">
          KipaBiz v2.1 · Last updated {new Date(form.updated_at).toLocaleString("en-NG")}
        </div>
      </div>

      {showRename && (
        <RenameAccountantModal
          onClose={() => setShowRename(false)}
          onRenamed={(newName) => {
            setToast(`Done! Your accountant is now ${newName}`);
            setTimeout(() => setToast(""), 2500);
          }}
        />
      )}

      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-[var(--tx)] text-white px-5 py-3 rounded-2xl text-[13px] font-bold z-[60]"
          style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.25)", animation: "toastIn 200ms ease-out" }}
          data-testid="rename-toast"
        >
          {toast}
        </div>
      )}

      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translate(-50%, 10px); }
          to { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>

      <style>{`
        .s-inp {
          background: var(--sf2);
          border: 1.5px solid var(--br);
          border-radius: 12px;
          padding: 10px 14px;
          color: var(--tx);
          font-size: 14px;
          width: 100%;
          font-family: inherit;
          outline: none;
        }
        .s-inp:focus { border-color: var(--ac); }
        .btn-orange {
          background: var(--ac); color: #fff; border: none; border-radius: 14px;
          padding: 12px; font-weight: 800; font-size: 13px; cursor: pointer;
          font-family: inherit;
        }
        .btn-orange:disabled { opacity: 0.6; }
      `}</style>
    </div>
  );
}

function Section({ title, hint, children }) {
  return (
    <div className="bg-white border border-[var(--br)] rounded-2xl p-4 mb-3 card-sh">
      <div className="text-[13px] font-extrabold text-[var(--tx)] mb-1">{title}</div>
      {hint && <div className="text-[11px] text-[var(--tx3)] mb-3 leading-snug">{hint}</div>}
      <div>{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="mb-3">
      <div className="text-[11px] font-bold uppercase tracking-wider text-[var(--tx3)] mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-[var(--tx3)] mt-1 leading-snug">{hint}</div>}
    </div>
  );
}
